<template>
  <div class="cf-page-container">
    <div
      v-if="isOrderLoading"
      :class="['cf-container-full-width', { 'cf-page-loader': isOrderLoading }]"
    >
      <div class="cf-loader" />
    </div>

    <!-- Join Group Order Failure Message -->
    <div
      v-if="failedToLoadOrder"
      class="cf-page-content-container cf-card cf-flex cf-flex--column cf-flex--align-center cf-flex-justify-center cf-flex--gap-16"
    >
      <h1 class="cf-title cf-title--1">
        Unable to join group order.
      </h1>

      <p class="cf-subtitle cf-subtitle--2">
        Oops! Either this order has already closed or this URL is invalid. You can always start your own order by browsing the menu.
      </p>

      <router-link
        class="cf-button cf-button--primary cf-button--lg"
        :to="{
          name: 'orderMenu',
          params: {
            merchantId: $route.params.merchantId
          }
        }"
      >
        Browse Menu
      </router-link>
    </div>

    <div
      v-else-if="orderToJoin"
      class="cf-card cf-card--overflow-visible cf-flex cf-flex--column cf-flex--gap-16"
    >
      <div class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-16">
        <img
          class="cf-illustration cf-illustration--group-order"
          src="/images/illustrations/group-order-people.svg"
          role="presentation"
          alt=""
        >

        <div class="cf-flex cf-flex--column  cf-flex--align-center cf-flex--gap-12">
          <h1 class="cf-title cf-title--1">
            Would you like to join {{ orderToJoin.groupOrder.name }}?
          </h1>

          <p class="cf-subtitle cf-subtitle--2">
            You'll share a cart with your group. The host will checkout once everyone is ready.
          </p>

          <div
            v-if="!joiningGroup && order.orderItems.length > 0"
            class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-center cf-flex--gap-10 cf-alt-background"
          >
            <span class="cf-icon cf-icon--red far fa-exclamation-circle" />

            <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
              Your current cart will be replaced.
            </p>
          </div>
        </div>
      </div>

      <!-- Group Order Location Details -->
      <div class="cf-card cf-flex cf-full-width cf-clear-padding">
        <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-center cf-flex--gap-12 cf-flex--no-shrink cf-padding cf-border-right">
          <img
            v-if="orderDetailsContent.imageUrl"
            :src="orderDetailsContent.imageUrl"
            :alt="orderDetailsContent.name"
            class="cf-order-mode-image"
          >
          <span
            v-else
            :class="`cf-icon primary far ${ orderDetailsContent.icon }`"
          />

          <div class="cf-flex cf-flex--column">
            <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
              {{ orderDetailsContent.name }}
            </p>
          </div>
        </div>

        <div class="cf-grid cf-grid--2-grid-column-left-large cf-full-width cf-padding-12">
          <div class="cf-flex cf-flex--column cf-flex--align-start cf-flex--justify-center cf-max-width-300">
            <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy cf-no-wrap-ellipsis">
              {{ orderToJoin.store.description }}
            </p>

            <p class="cf-subtitle cf-subtitle--2 cf-no-wrap-ellipsis">
              {{ orderToJoinStoreAddress }}
            </p>
          </div>

          <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-end">
            <cf-store-directions
              :address="orderToJoin.store.address"
              :show-button-content="false"
              show-landmark-or-cross-street
            />
          </div>
        </div>
      </div>

      <!-- Join Order Name and Phone Inputs -->
      <cf-validated-form
        name="joinGroup"
        @valid-submit="joinGroup"
      >
        <div class="cf-flex cf-flex--column cf-flex--gap-16">
          <div class="cf-flex cf-flex--row cf-flex--gap-10">
            <cf-text-input
              v-model="displayName"
              type="text"
              label="Your Name"
              placeholder=""
              name="displayName"
              icon-class="fa-user"
              :validations="{
                max: 20,
                required: true
              }"
            />

            <cf-phone-number-input
              v-model="phoneNumber"
              label="Phone Number"
              name="phone"
              :validations="{
                required: true
              }"
            />
          </div>

          <div class="cf-flex cf-flex--row cf-flex--justify-center">
            <div class="cf-grid cf-grid--2-grid-column-equal cf-grid--gap-16 cf-mobile-column cf-max-width-600">
              <button
                :class="['cf-button cf-button--primary cf-button--lg cf-button--auto', { 'cf-button--loading': joiningGroup }]"
                type="submit"
              >
                Join Group Order
              </button>

              <router-link
                class="cf-button cf-button--secondary cf-button--lg cf-button--auto"
                :to="{
                  name: 'orderMenu',
                  params: {
                    merchantId: $route.params.merchantId
                  }
                }"
              >
                Cancel
              </router-link>
            </div>
          </div>
        </div>
      </cf-validated-form>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';
  import http from '@/services/http';
  import parsePhoneFromApi from '@/helpers/parse-phone-from-api';
  import { formatAddressInline } from '@/helpers/address-format';

  import CfPhoneNumberInput from '@/view/components/shared/cf-phone-number-input/index.vue';
  import CfStoreDirections from '@/view/components/shared/cf-store-directions/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';
  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';


  export default {
    name: 'JoinGroupOrderPage',

    metaInfo() {
      return {
        title: 'Join Group Order'
      };
    },

    components: {
      CfPhoneNumberInput,
      CfStoreDirections,
      CfTextInput,
      CfValidatedForm
    },

    data() {
      return {
        displayName: null,
        failedToLoadOrder: false,
        joiningGroup: false,
        isOrderLoading: false,
        orderToJoin: null,
        phoneNumber: null
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('orders', ['order']),
      ...mapGetters('session', ['currentUser', 'orderDetails']),

      orderToJoinStoreAddress() {
        const addr = this.orderToJoin?.store?.address;
        if (!addr) return '';
        return formatAddressInline(addr, addr.countryCode);
      },

      orderDetailsContent() {
        const { orderType, fulfillmentType, pickupType } = this.orderToJoin;
        const { metadata } = this.content.OrderModes;

        switch (true) {
          case orderType === 'table' || fulfillmentType === 'dine-in':
            return { name: metadata.menuTypeLabelDineIn, icon: 'fa-utensils', imageUrl: metadata.dineInImageUrl };

          case orderType === 'catering' && fulfillmentType === 'delivery':
            return { name: metadata.cateringDeliveryLabel, icon: 'fa-truck', imageUrl: metadata.cateringDeliveryImageUrl };

          case orderType === 'catering':
            return { name: metadata.cateringPickupLabel, icon: 'fa-hat-chef', imageUrl: metadata.cateringPickupImageUrl };

          case fulfillmentType === 'delivery':
            return { name: metadata.menuTypeLabelDelivery, icon: 'fa-car', imageUrl: metadata.deliveryImageUrl };

          case fulfillmentType === 'shipping':
            return { name: metadata.menuTypeLabelShipping, icon: 'fa-truck', imageUrl: metadata.shippingImageUrl };

          case fulfillmentType === 'room-service':
            return { name: metadata.menuTypeLabelRoomService, icon: 'fa-door-closed', imageUrl: metadata.roomServiceImageUrl };

          case pickupType === 'curbside':
            return { name: metadata.menuTypeLabelCurbsidePickup || 'Curbside Pickup', icon: 'fa-car-side', imageUrl: metadata.curbsidePickupImageUrl };

          case pickupType === 'drive-through':
            return { name: metadata.menuTypeLabelDriveThruPickup || 'Drive Thru Pickup', icon: 'fa-car', imageUrl: metadata.driveThruPickupImageUrl };

          default:
            return { name: metadata.menuTypeLabelPickup, icon: 'fa-shopping-bag', imageUrl: metadata.pickupImageUrl };
        }
      }
    },

    watch: {
      'currentUser.accountId': 'autofillDetails'
    },

    created() {
      this.createdHandler();
    },

    methods: {
      autofillDetails() {
        if (this.currentUser.accountId) {
          this.displayName = this.currentUser.firstName;
          this.phoneNumber = parsePhoneFromApi(this.currentUser.primarySmsNumber);
        }
      },

      createdHandler() {
        this.autofillDetails();

        this.loadGroup();
      },

      handleGroupJoinSuccess() {
        events.emit('notification', {
          type: 'success',
          message: 'You have joined the group order!'
        });

        this.$router.replace({
          name: 'orderMenu',
          params: {
            merchantId: this.$route.params.merchantId
          }
        });
      },

      async loadGroup() {
        try {
          const alreadyJoinedGroup = this.order.groupOrder.token === this.$route.params.groupToken;

          if (alreadyJoinedGroup) {
            this.handleGroupJoinSuccess();
          }

          else {
            this.isOrderLoading = true;
            const response = await http.get(`/cardfree-api/v1/account/me/order/group/${this.$route.params.groupToken}`);
            this.orderToJoin = response.data;
          }
        }

        catch (error) {
          errorHandlers.silent(error);

          this.failedToLoadOrder = true;
        }

        finally {
          this.isOrderLoading = false;
        }
      },

      async joinGroup() {
        this.joiningGroup = true;

        try {
          await this.$store.dispatch('orders/groupOrderJoin', {
            displayName: this.displayName,
            groupToken: this.$route.params.groupToken,
            phoneNumber: this.phoneNumber
          });

          this.handleGroupJoinSuccess();
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.joiningGroup = false;
        }
      }
    }
  };
</script>
